import { Injectable } from '@angular/core';
import {
  emptyExistingAccount,
  ExistingAccount,
} from '@rivethealth/data/account';
import { accountIdPathParam } from '@rivethealth/data/query-params';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccountService } from '../../api/account.service';
import { useSubscriptionLatest } from '../../common/core/hooks';
import { Loadable, loadMap, success } from '../../common/helpers/loadable';
import { RequiredNumberParam } from '../../common/state/route';
import { RouteService, usePathParam } from '../../common/state/route.service';

@Injectable()
export class AccountState {
  private readonly reload_ = new BehaviorSubject(undefined);

  constructor(
    private readonly accountService: AccountService,
    private readonly routeService: RouteService,
  ) {}

  readonly id = this.routeService.getPath(
    new RequiredNumberParam(accountIdPathParam),
  );

  get accountId() {
    return useAccountId(this.routeService);
  }

  readonly load: Observable<Loadable<ExistingAccount>> = combineLatest([
    this.id,
    this.reload_,
  ]).pipe(
    loadMap(([accountId]) =>
      this.accountService
        .getAccount(accountId)
        .pipe(map(account => ({ ...account, id: accountId }))),
    ),
  );

  reload() {
    this.reload_.next(undefined);
  }
}

export function useAccount(accountState: AccountState) {
  return useSubscriptionLatest(accountState.load.pipe(success()), []);
}

export function useAccountOrDefault(
  accountState: AccountState,
): ExistingAccount {
  const loadedAccount = useAccount(accountState);
  return loadedAccount || emptyExistingAccount;
}

export function useAccountId(routeService: RouteService) {
  return usePathParam(
    routeService,
    new RequiredNumberParam(accountIdPathParam),
  );
}
